import { observable, action } from 'mobx'
import { API, IUserResponse, SecurityRole, ILocation } from '@getgreenline/homi-shared'

export class EmployeeFilterStore {
  @observable
  users: IUserResponse[] = []

  @observable
  securityRoles: SecurityRole[] = []

  @observable
  locations: ILocation[] = []

  @observable
  searchText?: string

  @observable
  filteredLocationIds: number[] = []

  @observable
  filteredSecurityRoleIds: number[] = []

  @observable
  isLoading = false

  @observable
  isFiltered = false

  @observable
  filteredUsers: IUserResponse[] = []

  @action
  setUsers = (users: IUserResponse[]) => {
    this.isLoading = true
    this.users = users
    this.isLoading = false
  }

  @action
  setSecurityRoles = (securityRoles: SecurityRole[]) => {
    this.securityRoles = securityRoles
  }

  @action
  setLocations = (locations: ILocation[]) => {
    this.locations = locations
  }

  @action
  setSearchText = (searchText?: string) => {
    this.searchText = searchText
  }

  @action
  setFilteredLocationIds = (filteredLocationIds?: number[]) => {
    this.filteredLocationIds = filteredLocationIds || []
  }

  @action
  setFilteredSecurityRoleIds = (filteredSecurityRoleIds?: number[]) => {
    this.filteredSecurityRoleIds = filteredSecurityRoleIds || []
  }

  @action
  setFilteredUsers() {
    this.isLoading = true
    let filteredUsers = this.users
    if (this.searchText) {
      filteredUsers = this.filterBySearch(filteredUsers)
    }

    if (this.filteredLocationIds.length > 0) {
      filteredUsers = this.filterByLocation(filteredUsers)
    }

    if (this.filteredSecurityRoleIds.length > 0) {
      filteredUsers = this.filterBySecurityRole(filteredUsers)
    }
    this.filteredUsers = filteredUsers
    this.isLoading = false
  }

  @action
  applyFilter = () => {
    if (
      this.searchText ||
      this.filteredSecurityRoleIds.length > 0 ||
      this.filteredLocationIds.length > 0
    ) {
      this.isFiltered = true
      this.setFilteredUsers()
    } else {
      this.isFiltered = false
    }
  }

  resetFilter = () => {
    this.setSearchText()
    this.setFilteredLocationIds()
    this.setFilteredSecurityRoleIds()
    this.isFiltered = false
  }

  filterBySearch = (users: IUserResponse[]): IUserResponse[] => {
    if (!this.searchText) return users

    const search = this.searchText.toLowerCase().trim()
    const filteredUsers = users.filter((u) => {
      const matchesEmail = u.email ? u.email.toLowerCase().includes(search) : false
      const matchesName = u.name ? u.name.toLowerCase().includes(search) : false
      const matchesPasscode = u.registerPasscode
        ? u.registerPasscode.toLowerCase().includes(search)
        : false
      return matchesEmail || matchesName || matchesPasscode
    })

    return filteredUsers
  }

  filterByLocation = (users: IUserResponse[]): IUserResponse[] => {
    const filteredLocationIds = this.filteredLocationIds

    if (!filteredLocationIds) return users

    const filteredUsers = users.reduce((acc: IUserResponse[], currentUser): IUserResponse[] => {
      const userAssignedLocationIds = currentUser.entityAssignments.map(
        (entities) => entities.entity.id,
      )
      const isMatch = userAssignedLocationIds.some((locationId) =>
        filteredLocationIds.includes(locationId),
      )
      return isMatch ? [...acc, currentUser] : acc
    }, [])

    return filteredUsers
  }

  filterBySecurityRole = (users: IUserResponse[]): IUserResponse[] => {
    const filteredSecurityRoleIds = this.filteredSecurityRoleIds
    if (!filteredSecurityRoleIds) return users

    const filteredUsers = users.reduce((acc: IUserResponse[], currentUser) => {
      const userAssignedRoleIds = currentUser.entityAssignments.map(
        (entities) => entities.securityRoleId,
      )
      const isMatch = userAssignedRoleIds.some((roleId) => {
        if (!roleId) return true // return all
        return filteredSecurityRoleIds.includes(roleId)
      })

      return isMatch ? [...acc, currentUser] : acc
    }, [])
    return filteredUsers
  }
}
